import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Mail, Send, Trash2, Users } from 'lucide-react';
import AdminSection from '../components/AdminSection';
import Pagination from '../components/Pagination';
import { useServerPaged } from '../hooks/use-paged';
import { fetchSubscribers, deleteSubscriber, sendCampaign } from '../lib/adminApi';

const inputCls =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10';

export default function AdminNewsletter() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ subject: '', body: '' });
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const { page, setPage, pageSize, setPageSize, skip, limit } = useServerPaged({ initialSize: 25 });
  const listQuery = useQuery({
    queryKey: ['admin', 'newsletter', skip, limit],
    queryFn: () => fetchSubscribers({ skip, limit }),
    placeholderData: keepPreviousData,
  });
  const data = listQuery.data;
  const pagedSubs = data?.items || [];
  const pageCount = Math.max(1, Math.ceil((data?.total || 0) / pageSize));

  const delMut = useMutation({
    mutationFn: (id) => deleteSubscriber(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'newsletter'] }),
  });
  const sendMut = useMutation({
    mutationFn: () => sendCampaign(form.subject.trim(), form.body.trim()),
    onSuccess: (r) => setMsg({ ok: `Campaña enviada: ${r.sent} correos${r.failed ? ` · ${r.failed} fallidos` : ''}.`, err: '' }),
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo enviar.' }),
  });

  const send = (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) { setMsg({ ok: '', err: 'Asunto y mensaje son obligatorios.' }); return; }
    if (!window.confirm(`¿Enviar la campaña a ${data?.active ?? 0} suscriptores activos?`)) return;
    setMsg({ ok: '', err: '' });
    sendMut.mutate();
  };

  const exportCsv = async () => {
    // Export the whole list, not just the current page.
    const all = await fetchSubscribers({ limit: 5000 });
    const rows = [['email', 'nombre', 'idioma', 'activo', 'alta'], ...(all.items || []).map((s) => [s.email, s.name || '', s.lang || '', s.active ? 'sí' : 'no', s.created_at || ''])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'suscriptores-owa.csv'; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <AdminSection eyebrow="Admin" title="Newsletter" description="Suscriptores y envío de campañas por email (usa tu SMTP de Ajustes → Correo)." widthClassName="max-w-6xl">
      {msg.ok ? <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{msg.ok}</div> : null}
      {msg.err ? <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{msg.err}</div> : null}

      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5"><div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#f4fbfa] text-[#1fa5a3]"><Users className="h-5 w-5" /></div><div><p className="text-2xl font-bold text-[#14213d]">{data?.active ?? '—'}</p><p className="text-xs uppercase tracking-[0.15em] text-slate-500">Suscriptores activos</p></div></div></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5"><div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><Mail className="h-5 w-5" /></div><div><p className="text-2xl font-bold text-[#14213d]">{data?.total ?? '—'}</p><p className="text-xs uppercase tracking-[0.15em] text-slate-500">Total (incl. bajas)</p></div></div></div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        {/* Campaign */}
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <h2 className="text-xl font-bold text-[#14213d]">Nueva campaña</h2>
          <form onSubmit={send} className="mt-4 space-y-3">
            <input className={inputCls} placeholder="Asunto" value={form.subject} onChange={(e) => setForm((c) => ({ ...c, subject: e.target.value }))} required />
            <textarea className={`${inputCls} min-h-48`} placeholder="Mensaje (texto o HTML sencillo)…" value={form.body} onChange={(e) => setForm((c) => ({ ...c, body: e.target.value }))} required />
            <p className="text-xs text-slate-500">Se añade automáticamente el enlace de baja al pie de cada correo.</p>
            <button type="submit" disabled={sendMut.isPending} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-60">
              <Send className="h-4 w-4" />{sendMut.isPending ? 'Enviando…' : `Enviar a ${data?.active ?? 0} suscriptores`}
            </button>
          </form>
        </section>

        {/* Subscribers */}
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#14213d]">Suscriptores</h2>
            {(data?.total ?? 0) ? <button type="button" onClick={exportCsv} className="text-sm font-semibold text-[#1fa5a3] hover:underline">Exportar CSV</button> : null}
          </div>
          <div className="divide-y divide-slate-100">
            {pagedSubs.map((s) => (
              <div key={s.id} className="flex items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[#14213d]">{s.email}</p>
                  <p className="text-xs text-slate-500">{s.name || 'Sin nombre'} · {s.lang || 'es'}</p>
                </div>
                {!s.active ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">baja</span> : null}
                <button type="button" onClick={() => { if (window.confirm(`¿Eliminar ${s.email}?`)) delMut.mutate(s.id); }} className="rounded-full border border-slate-200 p-1.5 text-slate-400 hover:border-red-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {!pagedSubs.length ? <p className="py-8 text-center text-sm text-slate-500">Sin suscriptores todavía.</p> : null}
          </div>
          <Pagination page={page} pageCount={pageCount} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} pageSizes={[25, 50, 100]} />
        </section>
      </div>
    </AdminSection>
  );
}
